import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Tab,
    Tabs,
    Tooltip,
    Typography
} from "@mui/material";
import {
    Fragment,
    useEffect,
    useState
} from "react";
import {
    LinkAction,
    LinkRegistry,
    Location,
    Property
} from "../../models";

import EmailIcon from '@mui/icons-material/Email';
import { ExternalWindowPopUp } from "../general/popout/ExternalWindowPopUp";
import { GraphQLLinkRegistryDAOFactory } from "../../lib/linkRegistry/dao/GraphQLLinkRegistryDAOFactory";
import { LinkRegistryDAO } from "../../lib/linkRegistry/dao/LinkRegistryDAO";
import LoadingButton from "@mui/lab/LoadingButton";
import PrintIcon from '@mui/icons-material/Print';
import { PrintableLinkRegistryQRList } from "../linkRegistry/PrintableLinkRegistryQRList";
import QrCodeIcon from "@mui/icons-material/QrCode";
import { TENERA_SUPPORT_EMAIL } from "../general/ErrorFallback";
import { isMobile } from "react-device-detect";
import { useSnackbar } from "notistack";

type QRCodeButtonProps = {
    property: Property;
    locations: Array<Location>;
};

const linkRegistryDAO: LinkRegistryDAO = GraphQLLinkRegistryDAOFactory.getInstance();

type TabPanelProps = {
    children?: React.ReactNode;
    index: number;
    value: number;
};

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`qr-tabpanel-${index}`}
            aria-labelledby={`qr-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

export const QRCodeButton = ({ property, locations }: QRCodeButtonProps) => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [selectedLocationIds, setSelectedLocationIds] = useState<Array<string>>([]);
    const [showQRCodes, setShowQRCodes] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [assessmentLinks, setAssessmentLinks] = useState<Array<LinkRegistry>>([]);
    const [tabValue, setTabValue] = useState(0);
    const snackbar = useSnackbar();

    useEffect(() => {
        if (!isDialogOpen) {
            setTabValue(0);
            setSelectedLocationIds([]);
            setShowQRCodes(false);
        }
    }, [isDialogOpen]);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const generateLinks = async () => {
        try {
            setIsGenerating(true);
            const selectedLocations = locations.filter(location => selectedLocationIds.includes(location.id));
            const generatedLinks = await Promise.all(selectedLocations.map(async (location) => {
                return await linkRegistryDAO.create({
                    action: LinkAction.ASSESSMENT_FORM,
                    propertyId: property.id,
                    variables: JSON.stringify({
                        propertyId: property.id,
                        propertyAddress: property?.address,
                        locationId: location.id,
                        locationName: location.name
                    }),
                    published: true
                });
            }));
            setAssessmentLinks(generatedLinks);
            setIsDialogOpen(false);
            setSelectedLocationIds([]);
            setShowQRCodes(true);
        } catch (error) {
            snackbar.enqueueSnackbar("Failed to generate assessment codes", { variant: "error" });
        } finally {
            setIsGenerating(false);
        }
    };

    const handleRequestQR = async () => {
        if (selectedLocationIds.length === 0) return;

        setIsGenerating(true);
        try {
            const selectedLocations = locations.filter(loc => selectedLocationIds.includes(loc.id));

            // Prepare email content
            const emailSubject = `QR Code Request for ${property.name}`;
            const emailBody = `Hi Support Team,\n\n` +
                `I'd like to request QR codes for the following property and locations:\n\n` +
                `Property ID: ${property.id}\n\n` +
                `Locations:\n` +
                `${selectedLocations.map(loc => `- ${loc.name} (ID: ${loc.id})`).join('\n')}\n\n` +
                `Please let me know if you need any additional information to process this request.\n\n` +
                `Thank you,\n` +
                `[Your Name]`;

            // Create mailto link
            const mailtoLink = `mailto:${TENERA_SUPPORT_EMAIL}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;

            // Open email client
            window.location.href = mailtoLink;
            setIsDialogOpen(false);
        } catch (error) {
            snackbar.enqueueSnackbar("Failed to open email client", { variant: "error" });
        } finally {
            setIsGenerating(false);
        }
    };

    return (
        <>
            <Tooltip title={locations?.length === 0 ? "No locations available for QR codes" : null} enterTouchDelay={0}>
                <span>
                    <Button
                        variant="outlined"
                        color="inherit"
                        sx={{ textTransform: 'none' }}
                        onClick={() => setIsDialogOpen(true)}
                        startIcon={<QrCodeIcon />}
                        disabled={locations?.length === 0}
                    >
                        Need QR codes?
                    </Button>
                </span>
            </Tooltip>

            <Dialog
                open={isDialogOpen}
                onClose={() => setIsDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                fullScreen={isMobile}
            >
                <DialogTitle>QR Code Options</DialogTitle>
                <DialogContent>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs value={tabValue} onChange={handleTabChange} aria-label="QR code options" variant={isMobile ? "fullWidth" : "standard"}>
                            <Tab label="Generate QR Codes" />
                            <Tab label="Request High-Quality QR Codes" />
                        </Tabs>
                    </Box>

                    <TabPanel value={tabValue} index={0}>
                        <Typography variant="body2" color="text.secondary" paragraph>
                            Select locations to generate printable QR codes. These can be printed and used immediately.
                        </Typography>
                    </TabPanel>

                    <TabPanel value={tabValue} index={1}>
                        <Typography variant="body2" color="text.secondary" paragraph>
                            Select locations to request high-quality QR codes from our team. We'll reach out to you shortly.
                        </Typography>
                    </TabPanel>

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={selectedLocationIds.length > 0 && selectedLocationIds.length === locations.length}
                                onChange={(e, checked) => {
                                    setSelectedLocationIds(checked ? locations.map(location => location.id) : []);
                                }}
                                disabled={locations.length === 0}
                            />
                        }
                        label={<Typography>Select All</Typography>}
                        sx={{ mb: 2 }}
                    />

                    <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                        {locations.map((location, index) => (
                            <Fragment key={location.id}>
                                <Box display="flex" alignItems="center" py={1}>
                                    <Checkbox
                                        checked={selectedLocationIds.includes(location.id)}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setSelectedLocationIds(prev => [...prev, location.id]);
                                            } else {
                                                setSelectedLocationIds(prev => prev.filter(id => id !== location.id));
                                            }
                                        }}
                                    />
                                    <Box display="flex" flexDirection="column" ml={1}>
                                        <Typography>{location.name}</Typography>
                                    </Box>
                                </Box>
                                {index < locations.length - 1 && <Divider />}
                            </Fragment>
                        ))}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                    <LoadingButton
                        onClick={tabValue === 0 ? generateLinks : handleRequestQR}
                        loading={isGenerating}
                        disabled={selectedLocationIds.length === 0}
                        startIcon={tabValue === 0 ? <PrintIcon /> : <EmailIcon />}
                        variant="contained"
                    >
                        {tabValue === 0 ? 'Generate & Print' : 'Send Request'}
                    </LoadingButton>
                </DialogActions>
            </Dialog>

            {showQRCodes && (
                <ExternalWindowPopUp onClose={() => setShowQRCodes(false)}>
                    <PrintableLinkRegistryQRList links={assessmentLinks} />
                </ExternalWindowPopUp>
            )}
        </>
    );
}; 